// @ts-nocheck

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import scrollIntoView from 'scroll-into-view-if-needed';
import styled, { css } from 'styled-components';

import { Target } from '../../client';
import { BetGroupFactoryContext } from '../../contexts/BetGroupFactoryContext';
import { useExpandedBetGroupContext } from '../../contexts/ExpandedBetGroupContext';
import useMatchViewContext from '../../hooks/useMatchViewContext';
import usePrevious from '../../hooks/usePrevious';
import theme from '../../styled-components/dark';
import { BetTemplates } from '../../types/bet';
import type { BetTemplate } from '../../types/bet';
import {
  type BetGroupOutcome,
  type BetGroupPlayer,
  PresentationModes,
  type PresentationItem,
} from '../../types/betGroup';
import { CustomBetTemplates } from '../../types/betTemplate';
import type { CustomBetTemplate } from '../../types/betTemplate';
import { extrudePropertiesFromHash } from '../../utils/location';
import isMatch from '../../utils/routes/isMatch';
import RoutesRedirectionFactory from '../../utils/routes/RoutesRedirectionFactory';
import { SMOOTH_SCROLL_TIMEOUT, smartScrollToRootAnchor } from '../../utils/smoothScroll';
import BetGroupColumns from '../BetGroupColumns';
import BetGroupLines from '../BetGroupLines';
import BetGroupList from '../BetGroupList';
import BetGroupMultiline from '../BetGroupMultiline';
import ExpandButton from '../ExpandButton';

const MAX_OUTCOMES_TO_REDIRECT = 20;
const scrollOptions = { behavior: 'auto', block: 'start', scrollMode: 'if-needed' };

const betTemplateColumn = [BetTemplates.SET_SCORE, BetTemplates.SCORE];
const betTemplateList = [
  BetTemplates.LIST_ODD,
  BetTemplates.LIST,
  CustomBetTemplates.SOCCER_SCORERS_AND_TEAM_WINS,
  CustomBetTemplates.SOCCER_SCORERS_OVERTIME,
  CustomBetTemplates.SOCCER_CARD_PLAYER,
  CustomBetTemplates.MULTICHOICE_MM_SOCCER_SCORERS,
  CustomBetTemplates.MULTICHOICE_MM_ICE_HOCKEY_SCORERS,
  CustomBetTemplates.MULTICHOICE_MM_RUGBY_SCORERS,
  CustomBetTemplates.MULTICHOICE_MM_FOOTBALL_SCORERS,
];
const betTemplateMultiline = [
  BetTemplates.ASIAN_HANDICAP,
  BetTemplates.ASIAN_TEXT_HANDICAP,
  BetTemplates.OVER_UNDER,
  CustomBetTemplates.SOCCER_SCORERS,
  CustomBetTemplates.SOCCER_GOAL_TYPE,
  CustomBetTemplates.SOCCER_COMPETITOR_GOAL_TYPE,
  CustomBetTemplates.SOCCER_VERSUS,
  CustomBetTemplates.SOCCER_MULTIPLE_SCORERS,
  CustomBetTemplates.SOCCER_SCORERS_PERIOD,
  CustomBetTemplates.SOCCER_PLAYER_PROPS,
  CustomBetTemplates.SOCCER_DECISIVE_PLAYERS,
  CustomBetTemplates.RUGBY_SCORERS,
  CustomBetTemplates.RUGBY_MULTIPLE_SCORERS,
  CustomBetTemplates.ICE_HOCKEY_MULTIPLE_SCORERS,
  CustomBetTemplates.ICE_HOCKEY_SCORERS,
  CustomBetTemplates.FOOTBALL_SCORERS,
  CustomBetTemplates.BASKETBALL_POINT_PLAYER,
  CustomBetTemplates.BASKETBALL_PLAYER_OVER_UNDER,
  CustomBetTemplates.HANDBALL_SCORERS_PLAYER,
];

const commonMargin = css`
  width: calc(100% - 1.2em);
  margin: 0.6em;
`;

const Wrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
`;

const StyledExpandButton = styled(ExpandButton)`
  ${commonMargin}
`;

const AlternateTitleWrapper = styled.div`
  margin-top: 0.2em;
  padding: 0 0.75em;
  display: flex;
  justify-content: center;
  align-items: center;

  ${({ $isMultiline }) => {
    if ($isMultiline) {
      return css`
        margin-bottom: 0.3em;
      `;
    }
  }}
`;

const AlternateTitleLabel = styled.div`
  color: ${theme.MatchCard.alternativeMainBetColor};
  padding: 0 0.75em;
`;

const AlternateTitleLeftBlock = styled.div`
  flex: 1;
  height: 1px;
  background-color: ${theme.MatchCard.alternativeMainBetSideBlocksBg};
`;

const AlternateTitleRightBlock = styled.div`
  flex: 1;
  height: 1px;
  background-color: ${theme.MatchCard.alternativeMainBetSideBlocksBg};
`;

type Item = BetGroupOutcome & BetGroupPlayer;

type BetGroupFactoryProps = {
  className?: string;
  expandPreviewLines: number | null;
  betTemplate: BetTemplate | CustomBetTemplate;
  items: Array<Item & Array<Item>>;
  matchIsAvailable: boolean;
  betTemplate: BetTemplate;
  showPercentDistribution: boolean;
  showOnlyStarters?: boolean;
  headers: Array<string>;
  betTypeId: number;
  betTitle: string;
  isAlternativeMainBet: boolean;
  showAlternativeTitle?: boolean;
  matchId: number;
  betId: number;
  hasStarters: boolean;
  betPresentationMode: PresentationItem;
  locationHash: string;
  locationPathname: string;
  isMultichoice: boolean;
  expandId: string;
  betCategoryId?: number;
};

const BetGroupFactory = ({
  expandPreviewLines,
  className,
  items,
  showAlternativeTitle = false,
  showOnlyStarters = false,
  matchId,
  betId,
  hasStarters,
  betPresentationMode,
  locationHash,
  locationPathname,
  isMultichoice,
  expandId,
  betCategoryId,
  ...rest
}: BetGroupFactoryProps) => {
  const { headerSize } = useMatchViewContext();
  const { betTemplate, betTitle, isAlternativeMainBet } = rest;
  // On n'empeche d'expand si le mode de présentation est SLIDER_BY_BET
  const shouldAllowExpand = useMemo(() => {
    if (betPresentationMode?.type !== PresentationModes.CLASSIC) {
      if (betPresentationMode?.type === PresentationModes.SLIDER_BY_BET) {
        return false;
      }
      if (betPresentationMode?.disableExpand) {
        return false;
      }
    }
    return expandPreviewLines;
  }, [betPresentationMode, expandPreviewLines]);

  // Sachant que la liste est virtualisée, on utilise un context extérieur à la liste de bet groups pour gérer les expands
  const { expandOnMount, expandedBetGroups, setExpandedBetGroups } = useExpandedBetGroupContext();
  const isExpand = useMemo(() => expandedBetGroups[expandId] ?? false, [expandedBetGroups, expandId]);
  const prevIsExpand = usePrevious(isExpand);

  const wrapperRef = useRef(null);
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const history = useHistory();

  const expandClickShouldRedirect = useMemo(
    () => !isMatch(locationPathname) && shouldRedirect,
    [locationPathname, shouldRedirect],
  );

  useEffect(() => {
    let redirectNeeded = false;
    let totalOutcomes = 0;

    for (const column of items) {
      if (Array.isArray(column)) {
        totalOutcomes += column.length;

        if (totalOutcomes >= MAX_OUTCOMES_TO_REDIRECT) {
          redirectNeeded = true;
          break;
        }
      }

      const { id: outcomeId, outcomes } = column;

      if (outcomeId) {
        if (items?.length >= MAX_OUTCOMES_TO_REDIRECT) {
          redirectNeeded = true;
          break;
        }
      }

      if (outcomes) {
        if (outcomes?.length >= MAX_OUTCOMES_TO_REDIRECT) {
          redirectNeeded = true;
          break;
        }
      }
    }

    setShouldRedirect(redirectNeeded);
  }, [items]);

  /**
   * This useEffect is used to watch on location state and to expand (or not) the betgroup depending
   * on weither or not the outcome is present (or not) in the location state is in the current (betgroup) items.
   */
  useLayoutEffect(() => {
    if (locationHash) {
      const { o: extrudedOutcomeIdAsStr, b: extrudedBetIdAsStr } = extrudePropertiesFromHash(locationHash);

      const extrudedOutcomeId = extrudedOutcomeIdAsStr ? parseInt(extrudedOutcomeIdAsStr, 10) : undefined;
      const extrudedBetId = extrudedBetIdAsStr ? parseInt(extrudedBetIdAsStr, 10) : undefined;

      let doItemsHaveTargetedOne = false;

      if (extrudedBetId === betId) {
        doItemsHaveTargetedOne = true;
      }

      if (extrudedOutcomeId) {
        doItemsHaveTargetedOne =
          doItemsHaveTargetedOne ||
          !!items.find((column) => {
            if (Array.isArray(column)) {
              return column.find((outcome) => outcome.id === extrudedOutcomeId);
            }

            const { id: outcomeId, outcomes } = column;

            if (outcomeId) {
              return outcomeId === extrudedOutcomeId;
            }

            if (outcomes) {
              return outcomes.find((innerOutcomeId) => innerOutcomeId.id === extrudedOutcomeId);
            }

            return null;
          });
      }

      setTimeout(async () => {
        setExpandedBetGroups((prev) => {
          prev[expandId] = doItemsHaveTargetedOne || prev[expandId];
          return { ...prev };
        });

        if (extrudedOutcomeId) {
          const anchorElem = await smartScrollToRootAnchor(
            `match-group-${matchId}-${extrudedOutcomeId}`,
            SMOOTH_SCROLL_TIMEOUT,
            Target.__MOBILE__ ? -headerSize : headerSize,
          );

          if (anchorElem) {
            const parentNode: unknown = anchorElem.parentElement;

            if (parentNode && extrudedOutcomeIdAsStr) {
              setTimeout(() => {
                const times = 3;
                const duration = 1500;
                parentNode.style.animation = `glow-with-drop-shadow ${duration}ms ease-in-out ${times}`;

                setTimeout(() => {
                  parentNode.style.animation = 'none';
                }, times * duration);
              }, 500);
            }
          }
        }
      }, 0);
    }
  }, [betId, expandId, headerSize, items, locationHash, matchId, setExpandedBetGroups]);

  const expandedOnMountRef = useRef(false);

  useLayoutEffect(() => {
    if (expandOnMount && !expandedOnMountRef.current) {
      setTimeout(() => {
        setExpandedBetGroups((prev) => {
          prev[expandId] = true;
          return { ...prev };
        });

        expandedOnMountRef.current = true;
      }, 0);
    }
  }, [expandId, expandOnMount, setExpandedBetGroups]);
  const finalItems = useMemo(() => {
    let result: Array<Item & Array<Item>> = [...items];
    // On regarde si on doit filtrer les titulaires
    if (showOnlyStarters) {
      result = result.filter((item) => !!item.starter);
    }

    if (shouldAllowExpand && expandPreviewLines && !isExpand) {
      if ([BetTemplates.SET_SCORE, BetTemplates.SCORE].indexOf(betTemplate) !== -1) {
        // $FlowFixMe
        result = result.map((column) => column.slice(0, expandPreviewLines));
      }
      result = result.slice(0, expandPreviewLines);
    }

    return result;
  }, [items, showOnlyStarters, shouldAllowExpand, expandPreviewLines, isExpand, betTemplate]);

  const onExpandClick = useCallback(() => {
    if (expandClickShouldRedirect) {
      const matchRoute = RoutesRedirectionFactory.getMatchRoutePathname(matchId);

      if (matchRoute) {
        if (typeof betCategoryId === 'number') {
          history.push(`${matchRoute}#bc=${betCategoryId}&b=${betId}`);
        } else {
          // Code mis en commentaire car provoque un problème lié à react-virtualized en version 9.8.0.
          // Sur la version 9.22.3, le problème n'est plus présent. Mais le bouton back ne scroll pas au bon index dans
          // la liste des matchs. Ceci est donc un quick fix temporaire
          //   history.push(`${matchRoute}#b=${betId}`);

          history.push(matchRoute);
        }
      }
    } else {
      setExpandedBetGroups((prev) => {
        prev[expandId] = !prev[expandId];
        return { ...prev };
      });
    }
  }, [expandClickShouldRedirect, matchId, betCategoryId, history, betId, setExpandedBetGroups, expandId]);

  useEffect(() => {
    if (wrapperRef.current && !isExpand && prevIsExpand) {
      scrollIntoView(wrapperRef.current, scrollOptions);
    }
  }, [isExpand, prevIsExpand]);

  const Component = useMemo(() => {
    if (finalItems.length) {
      if (~betTemplateColumn.indexOf(betTemplate)) {
        return BetGroupColumns;
      }

      if (~betTemplateList.indexOf(betTemplate)) {
        return BetGroupList;
      }

      if (~betTemplateMultiline.indexOf(betTemplate)) {
        return BetGroupMultiline;
      }

      return BetGroupLines;
    }

    return null;
  }, [betTemplate, finalItems]);

  const forwardedState = useMemo(() => ({ isMultichoice }), [isMultichoice]);

  if (Component) {
    return (
      <BetGroupFactoryContext.Provider value={forwardedState}>
        <Wrapper
          ref={(ref) => {
            // $FlowFixMe
            wrapperRef.current = ref;
          }}
          className={className}
        >
          {showAlternativeTitle && isAlternativeMainBet ? (
            <AlternateTitleWrapper $isMultiline={!!~betTemplateMultiline.indexOf(betTemplate)}>
              <AlternateTitleLeftBlock />
              <AlternateTitleLabel>{betTitle}</AlternateTitleLabel>
              <AlternateTitleRightBlock />
            </AlternateTitleWrapper>
          ) : null}
          {/* $FlowFixMe cant't have item type to be BetGroupOutcome or BetGroupPlayer */}
          <Component
            {...rest}
            items={finalItems}
            className="bet-group-template"
            matchId={matchId}
            betPresentationMode={betPresentationMode}
            showOnlyStarters={showOnlyStarters}
            hasStarters={hasStarters}
            isMultichoice={isMultichoice}
          />
          {!!shouldAllowExpand && expandPreviewLines ? (
            <StyledExpandButton
              isOpen={isExpand}
              onClick={onExpandClick}
              expandClickShouldRedirect={expandClickShouldRedirect}
            />
          ) : null}
        </Wrapper>
      </BetGroupFactoryContext.Provider>
    );
  }
  return null;
};
BetGroupFactory.whyDidYouRender = {
  logOnDifferentValues: true,
  customName: 'BetGroupFactory',
};

export default React.memo<BetGroupFactoryProps>(BetGroupFactory);
